import { Accordion } from "@components/accordion/accordion";
import { Block } from "@components/block";
import { Grid, GridCol } from "@components/grid";
import styles from "../selectedUser.module.scss";
import { Config, DoubleText, Table } from "@components/table";
import { Button } from "@components/button";
import { useAdminUsers } from "@features/admin/users";
import {
  AdminUserTypes,
  PlatformMerchantInfo,
  PlatformTransactionInfo,
} from "@features/admin/users/api";
import { formatDate, formatTime } from "@helpers/date";
import { formatUsdt } from "@helpers/format";
import { EllipsisText } from "@components/ellipsis-text";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AddMerchantModal,
  AddTransactionModal,
  EditMerchantModal,
} from "./platformModals";
import { useBoolState } from "@hooks/use-bool-state";
import { Confirm } from "@components/confirm/confirm";

const transactionConfig: Config<PlatformTransactionInfo> = [
  {
    label: "Дата",
    source: "created_at_unix",
    renderComponent: (data) => (
      <DoubleText
        text={formatTime(new Date(data * 1000))}
        subText={formatDate(new Date(data * 1000))}
      />
    ),
  },
  {
    label: "Сумма",
    source: "amount",
    renderComponent: (data) => formatUsdt(data),
  },
  {
    label: "Хэш транзакции",
    source: "transaction_hash",
    renderComponent: (data) =>
      data && <EllipsisText text={data} copy startLength={10} endLength={10} />,
  },
  {
    label: "Ссылка",
    source: "blockchain_url",
    renderComponent: (data) =>
      data && (
        <EllipsisText
          text={data}
          copy
          startLength={10}
          endLength={10}
          link={data}
        />
      ),
  },
];

export const PlatformBlock = () => {
  const {
    state,
    editMerchantToPlatform,
    getAdminUserInfo,
    getPlatformTransactions,
  } = useAdminUsers();
  const { selectedUserId } = useParams();
  const [isAddModalOpen, showAddModal, hideAddModal] = useBoolState(false);
  const [isEditModalOpen, showEditModal, hideEditModal] = useBoolState(false);
  const [isTransactionModalOpen, showTransactionModal, hideTransactionModal] =
    useBoolState(false);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>("");
  const [toDeleteMerchantId, setToDeleteMerchantId] = useState<string>("");
  const [oldUserId, setOldUserId] = useState<string>("");

  // const [isCommissionModalOpen, showCommissionModal, hideCommissionModal] =
  //   useBoolState(false);
  const userKind = state.userInfo?.kind;

  useEffect(() => {
    if (selectedUserId == oldUserId && userKind == "platform")
      getPlatformTransactions(selectedUserId);

    setOldUserId(selectedUserId || "");
  }, [getPlatformTransactions, selectedUserId, userKind, oldUserId]);

  const onAddMerchantClick = () => {
    showAddModal();
  };

  const onAddTransactiontClick = () => {
    showTransactionModal();
  };

  const config: Config<PlatformMerchantInfo> = [
    {
      label: "Email мерчанта",
      source: "merchant_email",
    },
    // {
    //   label: "Комиссия",
    //   source: "commission",
    //   renderComponent: (data) => `${data}%`,
    // },
    {
      label: "Долг платформы",
      source: "debt",
      renderComponent: (data) => formatUsdt(data),
    },
    {
      label: "Наша прибыль",
      source: "our_ern_dept",
      renderComponent: (data) => formatUsdt(data),
    },
    {
      label: "Долг мерчанта",
      source: "merch_dept",
      renderComponent: (data) => formatUsdt(data),
    },
    {
      label: "Действия",
      source: "id",
      renderComponent: (data) => (
        <div style={{ display: "flex" }}>
          <Button
            style={{
              padding: "5px 7px",
              fontSize: "12px",
            }}
            onClick={() => {
              setSelectedMerchantId(data);
              showEditModal();
            }}
            buttonType="button"
          >
            Изменить комиссию
          </Button>
          <Button
            style={{
              padding: "5px 7px",
              fontSize: "12px",
              marginLeft: 8,
            }}
            type="danger"
            buttonType="button"
            onClick={() => {
              setToDeleteMerchantId(data);
            }}
          >
            Удалить
          </Button>
        </div>
      ),
    },
  ];

  const handleDeleteMerchant = () => {
    if (
      !selectedUserId ||
      !toDeleteMerchantId ||
      state.userInfo?.kind != AdminUserTypes.Platform
    )
      return;
    const merchantsData = state.userInfo?.merchants
      .filter((merchant) => merchant.id !== toDeleteMerchantId)
      .map((merchant) => ({
        merchant_id: merchant.id,
        commission_config: merchant.commission_config,
      }));
    if (!merchantsData) return;
    editMerchantToPlatform(selectedUserId, { merchants: merchantsData }).then(
      () => {
        setToDeleteMerchantId("");
        getAdminUserInfo(selectedUserId);
      }
    );
  };

  if (state.userInfo?.kind != AdminUserTypes.Platform) return null;

  return (
    <>
      <Block className={styles.block}>
        <h3 style={{ marginBottom: "15px" }}>Статистика запросов</h3>
        <Grid>
          <GridCol span={4}>
            <Block className={styles.cardBlock}>
              <div>Всего запросов</div>
              <span>{state.userInfo.total_requests}</span>
            </Block>
          </GridCol>
          <GridCol span={4}>
            <Block className={styles.cardBlock}>
              <div>Запросы с реквизитами</div>
              <span>{state.userInfo.requests_with_requisites}</span>
            </Block>
          </GridCol>
          <GridCol span={4}>
            <Block className={styles.cardBlock}>
              <div>Запросы без реквизитов</div>
              <span>{state.userInfo.requests_without_requisites}</span>
            </Block>
          </GridCol>
          <GridCol span={6}>
            <Block className={styles.cardBlock}>
              <div>Трастовый баланс</div>
              <span>{state.userInfo.trust_balance}</span>
            </Block>
          </GridCol>
          <GridCol span={6}>
            <Block className={styles.cardBlock}>
              <div>Замороженный баланс</div>
              <span>{state.userInfo.frozen_balance}</span>
            </Block>
          </GridCol>
        </Grid>
        <Accordion title="Статистика запросов">
          <Grid>
            <GridCol span={6}>
              <Block className={styles.cardBlock}>
                <div>Завершенные заказы</div>
                <span>{state.userInfo.orders_completed}</span>
              </Block>
            </GridCol>
            <GridCol span={6}>
              <Block className={styles.cardBlock}>
                <div>Отмененные заказы</div>
                <span>{state.userInfo.orders_canceled ?? 0}</span>
              </Block>
            </GridCol>
          </Grid>
        </Accordion>
        <Accordion title="Статистика споров">
          <Grid>
            <GridCol span={4}>
              <Block className={styles.cardBlock}>
                <div>Завершенные споры</div>
                <span>{state.userInfo.disputes_approved}</span>
              </Block>
            </GridCol>
            <GridCol span={4}>
              <Block className={styles.cardBlock}>
                <div>Отмененные споры</div>
                <span>{state.userInfo.disputes_canceled}</span>
              </Block>
            </GridCol>
            <GridCol span={4}>
              <Block className={styles.cardBlock}>
                <div>Открытые споры</div>
                <span>{state.userInfo.disputes_opened}</span>
              </Block>
            </GridCol>
          </Grid>
        </Accordion>
        <Accordion title="Статистика по суммам заказов">
          <Grid>
            <GridCol span={3}>
              <Block className={styles.cardBlock}>
                <div>От 1000 до 3000</div>
                <span>Отправлено: {state.userInfo.send_orders_1000}</span>
                <span>
                  Без Реквизитов:{" "}
                  {state.userInfo.orders_with_out_requisite_1000}
                </span>
              </Block>
            </GridCol>
            <GridCol span={3}>
              <Block className={styles.cardBlock}>
                <div>От 3000 до 5000</div>
                <span>Отправлено: {state.userInfo.send_orders_3000}</span>
                <span>
                  Без Реквизитов:{" "}
                  {state.userInfo.orders_with_out_requisite_3000}
                </span>
              </Block>
            </GridCol>
            <GridCol span={3}>
              <Block className={styles.cardBlock}>
                <div>От 5000 до 10000</div>
                <span>Отправлено: {state.userInfo.send_orders_5000}</span>
                <span>
                  Без Реквизитов:{" "}
                  {state.userInfo.orders_with_out_requisite_5000}
                </span>
              </Block>
            </GridCol>
            <GridCol span={3}>
              <Block className={styles.cardBlock}>
                <div>От 10000</div>
                <span>Отправлено: {state.userInfo.send_orders_10000}</span>
                <span>
                  Без Реквизитов:{" "}
                  {state.userInfo.orders_with_out_requisite_10000}
                </span>
              </Block>
            </GridCol>
          </Grid>
        </Accordion>
        {/* <form>
          <Button onClick={showCommissionModal}>Комиссии</Button>
        </form> */}
      </Block>
      <Block className={styles.block}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <h3>Мерчанты платформы</h3>
          <Button
            style={{ maxWidth: "200px" }}
            onClick={onAddMerchantClick}
            buttonType="button"
          >
            Добавить мерчантов
          </Button>
        </div>
        <Table data={state.userInfo.merchants} config={config}></Table>
      </Block>
      <Block className={styles.block}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <h3>Финансовые транзакции</h3>
          <Button
            style={{ maxWidth: "200px" }}
            onClick={onAddTransactiontClick}
            buttonType="button"
          >
            Добавить транзакцию
          </Button>
        </div>
        <Table
          data={state.platformTransactions}
          config={transactionConfig}
        ></Table>
      </Block>
      {selectedMerchantId !== "" && (
        <EditMerchantModal
          isOpen={isEditModalOpen}
          onClose={() => {
            hideEditModal();
            setSelectedMerchantId("");
          }}
          merchant_id={selectedMerchantId}
        />
      )}
      <AddMerchantModal isOpen={isAddModalOpen} onClose={hideAddModal} />
      <AddTransactionModal
        isOpen={isTransactionModalOpen}
        onClose={hideTransactionModal}
      />
      <Confirm
        isOpen={!!toDeleteMerchantId}
        description="Удаление мерчанта из платформы"
        onConfirm={() => {
          handleDeleteMerchant();
        }}
        onCancel={() => {
          setToDeleteMerchantId("");
        }}
        deleteLabel="Удалить"
        cancelLabel="Отмена"
      />
    </>
  );
};
